import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Boxes, ArrowLeftRight, Layers, Download, RefreshCw } from "lucide-react";

const num = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (d) => new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);

function toCSV(rows, cols) {
    const esc = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const head = cols.map((c) => esc(c.label)).join(",");
    const body = rows.map((r) => cols.map((c) => esc(c.get(r))).join(",")).join("\n");
    return head + "\n" + body;
}
function downloadCSV(name, csv) {
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
}

const TABS = [
    ["wall", "Wall-to-Wall Inventory", Boxes],
    ["movement", "Daily Movement", ArrowLeftRight],
    ["tonality", "Tonality On-Hand", Layers],
];

const WALL_COLS = [
    { label: "Item Code", get: (r) => r.item_code },
    { label: "Item Name", get: (r) => r.item_name },
    { label: "Group", get: (r) => r.item_group },
    { label: "Warehouse", get: (r) => r.warehouse_name || r.warehouse_code },
    { label: "On Hand", get: (r) => r.on_hand },
    { label: "Committed", get: (r) => r.committed },
    { label: "Available", get: (r) => r.available },
    { label: "On Order", get: (r) => r.on_order },
];
const MOVE_COLS = [
    { label: "Date", get: (r) => r.date },
    { label: "Document", get: (r) => r.doc_type },
    { label: "Direction", get: (r) => r.direction },
    { label: "Doc No.", get: (r) => r.doc_num },
    { label: "Item Code", get: (r) => r.item_code },
    { label: "Item Name", get: (r) => r.item_name },
    { label: "Warehouse", get: (r) => r.warehouse_code },
    { label: "Qty", get: (r) => r.qty },
    { label: "Signed Qty", get: (r) => r.signed_qty },
];
const TON_COLS = [
    { label: "Item Code", get: (r) => r.item_code },
    { label: "Item Name", get: (r) => r.item_name },
    { label: "Tonality", get: (r) => r.tonality },
    { label: "Warehouse", get: (r) => r.warehouse_code },
    { label: "On Hand", get: (r) => r.on_hand },
    { label: "Committed", get: (r) => r.committed },
    { label: "Available", get: (r) => r.available },
];

export default function InventoryReports() {
    const [schemas, setSchemas] = useState([]);
    const [schema, setSchema] = useState("");
    const [tab, setTab] = useState("wall");
    const [q, setQ] = useState("");
    const [dateFrom, setDateFrom] = useState(daysAgoISO(30));
    const [dateTo, setDateTo] = useState(todayISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/inquiry/reports/schemas");
                if (!alive) return;
                const rows = data?.schemas || [];
                setSchemas(rows);
                setSchema((cur) => cur || (rows[0]?.schema_name || ""));
            } catch (e) {
                if (alive) toast.error(formatApiError(e));
            }
        })();
        return () => { alive = false; };
    }, []);

    const run = async () => {
        if (!schema) return;
        setLoading(true);
        setData(null);
        try {
            let res;
            if (tab === "wall") {
                res = await api.get("/inquiry/reports/wall-to-wall", { params: { schemaName: schema, q } });
            } else if (tab === "movement") {
                res = await api.get("/inquiry/reports/daily-movement", { params: { schemaName: schema, date_from: dateFrom, date_to: dateTo } });
            } else {
                res = await api.get("/inquiry/reports/tonality", { params: { schemaName: schema, q } });
            }
            setData(res.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    // Load whenever the report or the company changes; date/search reloads are manual (Run).
    useEffect(() => {
        if (schema) run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, schema]);

    const items = data?.items || [];
    const cols = tab === "wall" ? WALL_COLS : tab === "movement" ? MOVE_COLS : TON_COLS;
    const schemaLabel = schemas.find((s) => s.schema_name === schema)?.display_name || schema;

    const doExport = () => {
        if (!items.length) { toast.error("Nothing to export yet."); return; }
        const stamp = todayISO();
        const base = tab === "wall" ? "Wall-to-Wall" : tab === "movement" ? "Daily-Movement" : "Tonality-On-Hand";
        downloadCSV(`${base}_${schema}_${stamp}.csv`, toCSV(items, cols));
    };

    return (
        <div className="space-y-4" data-testid="inventory-reports">
            {/* report picker */}
            <div className="inline-flex rounded-md border border-border overflow-hidden">
                {TABS.map(([key, label, Icon], i) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={`px-3 py-1.5 text-sm inline-flex items-center gap-1.5 ${i > 0 ? "border-l border-border" : ""} ${tab === key ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}
                        data-testid={`invrep-tab-${key}`}
                    >
                        <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                ))}
            </div>

            {/* controls */}
            <div className="sc-card p-3 md:p-4 flex flex-wrap items-end gap-3">
                <div className="min-w-[220px]">
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Company</label>
                    <select className="sc-input mt-1" value={schema} onChange={(e) => setSchema(e.target.value)} data-testid="invrep-schema">
                        {schemas.length === 0 && <option value="">No companies</option>}
                        {schemas.map((s) => <option key={s.schema_name} value={s.schema_name}>{s.display_name || s.schema_name}</option>)}
                    </select>
                </div>

                {tab === "movement" ? (
                    <>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">From</label>
                            <input type="date" className="sc-input mt-1" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="invrep-from" />
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">To</label>
                            <input type="date" className="sc-input mt-1" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="invrep-to" />
                        </div>
                    </>
                ) : (
                    <div className="min-w-[200px] flex-1">
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Search item</label>
                        <input className="sc-input mt-1" placeholder="Item code or name" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} data-testid="invrep-q" />
                    </div>
                )}

                <button className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60 inline-flex items-center gap-1.5" onClick={run} disabled={loading || !schema} data-testid="invrep-run">
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Run
                </button>
                <button className="px-3 py-1.5 rounded-md border border-border text-sm disabled:opacity-60 inline-flex items-center gap-1.5" onClick={doExport} disabled={!items.length} data-testid="invrep-export">
                    <Download className="w-4 h-4" /> Template (CSV)
                </button>
            </div>

            {/* result meta */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div>
                    {schemaLabel && <span className="font-medium text-foreground">{schemaLabel}</span>}
                    {data?.as_of && <span> · as of {new Date(data.as_of).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</span>}
                    {tab === "movement" && data && <span> · {data.date_from} to {data.date_to}</span>}
                </div>
                <div>{loading ? "Loading…" : `${num(items.length)} row${items.length === 1 ? "" : "s"}`}</div>
            </div>

            {data?.truncated && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Showing the first {num(items.length)} rows. Narrow the search or date range, or export the Template for the full set.
                </div>
            )}

            {/* table */}
            <div className="sc-card overflow-x-auto">
                <table className="w-full sc-table" style={{ minWidth: tab === "movement" ? 980 : 760 }}>
                    <thead>
                        <tr>
                            {cols.map((c) => (
                                <th key={c.label} className={/Qty|Hand|Committed|Available|Order|No\./.test(c.label) ? "text-right" : ""}>{c.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={cols.length} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
                        {!loading && items.length === 0 && <tr><td colSpan={cols.length} className="text-center py-8 text-muted-foreground">No rows. Choose a company{tab === "movement" ? " and date range" : " (and try a search)"}, then Run.</td></tr>}
                        {!loading && items.map((r, i) => (
                            <tr key={i}>
                                {cols.map((c) => {
                                    const v = c.get(r);
                                    const isNum = /Qty|Hand|Committed|Available|Order|No\./.test(c.label);
                                    const neg = c.label === "Signed Qty" && Number(v) < 0;
                                    return (
                                        <td key={c.label} className={`${isNum ? "sc-num" : ""} ${c.label === "Item Code" || c.label === "Tonality" ? "font-mono text-xs" : ""} ${neg ? "text-rose-600" : ""}`}>
                                            {isNum ? num(v) : (c.label === "Direction"
                                                ? <span className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${v === "IN" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{v}</span>
                                                : (v == null || v === "" ? "—" : v))}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="text-xs text-muted-foreground">
                Live from SAP (read-only). Wall-to-Wall is the current on-hand snapshot; Daily Movement lists every Delivery, Credit Memo, Goods Receipt, Goods Issue and Goods Receipt PO in the chosen dates; Tonality On-Hand breaks stock down by batch. "Template (CSV)" downloads the rows shown.
            </p>
        </div>
    );
}
